"use client";

import { useCallback, useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { rootStore } from "@store/rootStore";
import {
  readDailyMarketLastReadDate,
  writeDailyMarketLastReadDate,
} from "./dailyMarketReadStorage";
import { isDailyMarketUnread } from "./isDailyMarketUnread";
import { useDailyMarketSummary } from "./useDailyMarketSummary";

export interface UseDailyMarketReadStateResult {
  reportDate: string | null;
  lastReadDate: string | null;
  isUnread: boolean;
  /** 今日正文请求失败（isError）——日期不可得≠后端说没有报告，调用方可据此走错误态而非静默剔除 */
  isSummaryError: boolean;
  /** 今日正文请求进行中（含重试）——日期未知，调用方应挂起等待而非按「无报告」剔除 */
  isSummaryPending: boolean;
  /** Idempotent — writes yyyyMMdd to localStorage for current owner */
  markRead: (date?: string) => void;
}

export function useDailyMarketReadState(): UseDailyMarketReadStateResult {
  const { ownerInfo } = useSnapshot(rootStore.user);
  const ownerId = ownerInfo?.id ?? "";
  const {
    data: summary,
    isError: isSummaryError,
    isLoading: isSummaryPending,
  } = useDailyMarketSummary();
  const reportDate = summary?.date || null;

  const [lastReadDate, setLastReadDate] = useState<string | null>(() =>
    ownerId ? readDailyMarketLastReadDate(ownerId) : null,
  );

  useEffect(() => {
    setLastReadDate(ownerId ? readDailyMarketLastReadDate(ownerId) : null);
  }, [ownerId]);

  const markRead = useCallback(
    (date?: string) => {
      const targetDate = date ?? reportDate;
      if (!ownerId || !targetDate) {
        return;
      }
      writeDailyMarketLastReadDate(ownerId, targetDate);
      setLastReadDate(targetDate);
    },
    [ownerId, reportDate],
  );

  return {
    reportDate,
    lastReadDate,
    isUnread: isDailyMarketUnread(reportDate, lastReadDate),
    isSummaryError,
    isSummaryPending,
    markRead,
  };
}
